<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '../stores/notificationStore'
import type { INotification } from '../types/notification.types'
import { ERouteNames } from '@/router/ERouteNames'

/**
 * Чекбокс «выбрать всё» считается активным только когда выделены все элементы из
 * filteredItems (учитывая текущие фильтры) — иначе показывает indeterminate.
 */
const store = useNotificationStore()
const { selectedIds, filteredItems } = storeToRefs(store)
const router = useRouter()

const visibleCount = computed(() => filteredItems.value.length)
const selectedCount = computed(() => {
  /* Считаем только те выделенные id, которые сейчас видны в filteredItems —
     иначе индикация «все выбраны» путается при переключении фильтров. */
  const visibleIds = new Set<number>(filteredItems.value.map((item: INotification) => item.id))

  return Array.from(selectedIds.value).filter(id => visibleIds.has(id)).length
})

const allSelected = computed(() => visibleCount.value > 0 && selectedCount.value === visibleCount.value)
const someSelected = computed(() => selectedCount.value > 0 && !allSelected.value)
const hasSelection = computed(() => selectedCount.value > 0)

function onToggleSelectAll(): void {
  if (allSelected.value) {
    store.clearSelection()
  } else {
    store.selectAll()
  }
}

function onBulkRemove(): void {
  store.bulkRemove()
}

function onBulkRead(): void {
  store.bulkMarkRead()
}

function goToSettings(): void {
  router.push({ name: ERouteNames.NOTIFICATION_SETTINGS })
}
</script>

<template>
  <div class="notif-toolbar d-flex align-center">
    <div class="d-flex align-center gap-1">
      <VCheckbox
        :model-value="allSelected"
        :indeterminate="someSelected"
        hide-details
        density="compact"
        class="notif-toolbar__checkbox"
        @update:model-value="onToggleSelectAll"
      />

      <VTooltip text="Удалить выбранные" location="top">
        <template #activator="{ props: tooltipProps }">
          <IconBtn
            v-bind="tooltipProps"
            size="small"
            variant="text"
            :disabled="!hasSelection"
            @click="onBulkRemove"
          >
            <VIcon icon="ri-delete-bin-line" />
          </IconBtn>
        </template>
      </VTooltip>

      <VTooltip text="Отметить выбранные прочитанными" location="top">
        <template #activator="{ props: tooltipProps }">
          <IconBtn
            v-bind="tooltipProps"
            size="small"
            variant="text"
            :disabled="!hasSelection"
            @click="onBulkRead"
          >
            <VIcon icon="ri-mail-open-line" />
          </IconBtn>
        </template>
      </VTooltip>
    </div>

    <VSpacer />

    <VBtn
      variant="outlined"
      prepend-icon="ri-settings-3-line"
      @click="goToSettings"
    >
      Настройки
    </VBtn>
  </div>
</template>

<style scoped lang="scss">
.notif-toolbar {
  padding: 8px 16px;
  border-block-end: 1px solid rgba(var(--v-theme-on-surface), 0.12);

  &__checkbox {
    margin-inline-start: 4px;
  }
}
</style>
